import React from 'react'
import { sdk } from '@/lib/sdk'
import type { MenuCounters } from '@/types/sections'

/**
 * F-191 phase 2b: "novidades desde a sua última visita" for the menu. One
 * `GET /api/menu/counters` for the whole menu, shared by the full menu and the
 * rail. Opening a space zeroes its count here at once; the API records the
 * visit on the space's own `GET`, and the next refresh confirms it.
 */
interface Snapshot {
  spaces: Record<string, number>
  sections: Record<string, number>
}

const EMPTY: Snapshot = { spaces: {}, sections: {} }
let snapshot: Snapshot = EMPTY
/** Spaces seen in this tab since the last answer, kept at zero until the server agrees. */
const seen = new Map<string, string>()
const listeners = new Set<() => void>()
let inFlight: Promise<void> | null = null
let again = false

const emit = () => listeners.forEach((listener) => listener())
const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

const apply = (data: MenuCounters | null | undefined) => {
  const spaces: Record<string, number> = {}
  const sections: Record<string, number> = {}
  for (const row of data?.spaces ?? []) {
    if (!row?.spaceId) continue
    const count = seen.has(row.spaceId) ? 0 : Math.max(0, Number(row.count) || 0)
    spaces[row.spaceId] = count
    if (row.sectionId) sections[row.sectionId] = (sections[row.sectionId] ?? 0) + count
  }
  snapshot = { spaces, sections }
  emit()
}

/** Fetches the counters; a call during a fetch runs once more after it. */
export const refreshMenuCounters = (): Promise<void> => {
  if (inFlight) { again = true; return inFlight }
  inFlight = sdk.sections.menuCounters()
    .then((data) => {
      /* The server has the visits by now: what it says wins again. */
      seen.clear()
      apply(data)
    })
    .catch(() => undefined)
    .finally(() => {
      inFlight = null
      if (again) { again = false; void refreshMenuCounters() }
    })
  return inFlight
}

/** The person opened this space: its news are read. */
export const markSpaceSeen = (spaceId: string, sectionId: string) => {
  seen.set(spaceId, sectionId)
  const count = snapshot.spaces[spaceId]
  if (!count) return
  snapshot = {
    spaces: { ...snapshot.spaces, [spaceId]: 0 },
    sections: { ...snapshot.sections, [sectionId]: Math.max(0, (snapshot.sections[sectionId] ?? 0) - count) },
  }
  emit()
}

export const clearMenuCounters = () => {
  seen.clear()
  snapshot = EMPTY
  emit()
}

const readSnapshot = () => snapshot

export const useMenuCounters = (): Snapshot => React.useSyncExternalStore(subscribe, readSnapshot, readSnapshot)

/** The number a badge shows: "99+" above 99. */
export const badgeLabel = (count: number) => (count > 99 ? '99+' : String(count))
